import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './EnterpriseQuote.css';

interface QuoteFormData {
  companyName: string;
  department: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  position: string;
  expectedUsers: string;
  useCase: string;
  currentSolution: string;
  budget: string;
  timeline: string;
  requirements: string[];
  message: string;
}

const CorporateQuote: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<QuoteFormData>({
    companyName: '',
    department: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    position: '',
    expectedUsers: '',
    useCase: '',
    currentSolution: '',
    budget: '',
    timeline: '',
    requirements: [],
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const requirementOptions = [
    t('quote.corporate.requirements.sso'),
    t('quote.corporate.requirements.activeDirectory'),
    t('quote.corporate.requirements.customBranding'),
    t('quote.corporate.requirements.dedicatedServer'),
    t('quote.corporate.requirements.apiIntegration'),
    t('quote.corporate.requirements.auditLogs'),
    t('quote.corporate.requirements.multilingual'),
    t('quote.corporate.requirements.support24'),
    t('quote.corporate.requirements.training'),
    t('quote.corporate.requirements.dataMigration'),
    t('common.other')
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRequirementChange = (requirement: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.includes(requirement)
        ? prev.requirements.filter(req => req !== requirement)
        : [...prev.requirements, requirement]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/enterprise-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'corporate',
          ...formData
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);
      } else {
        throw new Error(t('quote.error.submitFailed'));
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alert(t('quote.error.submitFailedRetry'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="enterprise-quote">
        <div className="container">
          <div className="quote-success">
            <div className="success-icon">✓</div>
            <h1>{t('quote.success.title')}</h1>
            <p>
              {t('quote.success.corporate.message')}
            </p>
            <div className="success-actions">
              <button 
                onClick={() => window.close()}
                className="button-primary"
              >
                {t('common.close')}
              </button>
              <a href="/" className="button-secondary">
                {t('common.backToDashboard')}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-quote">
      <div className="container">
        <div className="quote-header">
          <h1>{t('quote.title')}</h1>
          <p className="quote-subtitle">{t('quote.corporate.subtitle')}</p>
          <p className="quote-description">
            {t('quote.corporate.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-section">
            <h2>{t('quote.corporate.sections.companyInfo')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="companyName">{t('quote.corporate.fields.companyName')} *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="department">{t('quote.corporate.fields.department')}</label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>{t('quote.sections.contactInfo')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="contactName">{t('quote.fields.name')} *</label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="position">{t('quote.fields.position')}</label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactEmail">{t('userSettings.email')} *</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contactPhone">{t('quote.fields.phone')}</label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>{t('quote.sections.usagePlan')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="expectedUsers">{t('quote.fields.expectedUsers')} *</label>
                <select
                  id="expectedUsers"
                  name="expectedUsers"
                  value={formData.expectedUsers}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="10-50">{t('quote.corporate.userCounts.small')}</option>
                  <option value="51-100">{t('quote.corporate.userCounts.medium')}</option>
                  <option value="101-300">{t('quote.corporate.userCounts.large')}</option>
                  <option value="301-500">{t('quote.corporate.userCounts.xlarge')}</option>
                  <option value="501-1000">{t('quote.corporate.userCounts.xxlarge')}</option>
                  <option value="1000+">{t('quote.corporate.userCounts.enterprise')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="timeline">{t('quote.fields.timeline')} *</label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('common.pleaseSelect')}</option>
                  <option value="immediate">{t('quote.timeline.immediate')}</option>
                  <option value="1month">{t('quote.timeline.oneMonth')}</option>
                  <option value="3months">{t('quote.timeline.threeMonths')}</option>
                  <option value="6months">{t('quote.timeline.sixMonths')}</option>
                  <option value="1year">{t('quote.timeline.oneYear')}</option>
                  <option value="undecided">{t('quote.timeline.undecided')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="useCase">{t('quote.fields.useCase')} *</label>
              <textarea
                id="useCase"
                name="useCase"
                value={formData.useCase}
                onChange={handleInputChange}
                placeholder={t('quote.corporate.placeholders.useCase')}
                rows={3}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="currentSolution">{t('quote.fields.currentSolution')}</label>
              <textarea
                id="currentSolution"
                name="currentSolution"
                value={formData.currentSolution}
                onChange={handleInputChange}
                placeholder={t('quote.placeholders.currentSolution')}
                rows={2}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>{t('quote.sections.budgetRequirements')}</h2>
            <div className="form-group">
              <label htmlFor="budget">{t('quote.fields.budget')}</label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
              >
                <option value="">{t('common.pleaseSelect')}</option>
                <option value="under-500k">{t('quote.corporate.budget.under500k')}</option>
                <option value="500k-1m">{t('quote.corporate.budget.500k1m')}</option>
                <option value="1m-3m">{t('quote.corporate.budget.1m3m')}</option>
                <option value="3m-5m">{t('quote.corporate.budget.3m5m')}</option>
                <option value="over-5m">{t('quote.corporate.budget.over5m')}</option>
                <option value="undecided">{t('quote.timeline.undecided')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('quote.fields.requirements')}</label>
              <div className="checkbox-grid">
                {requirementOptions.map((requirement) => (
                  <label key={requirement} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.requirements.includes(requirement)}
                      onChange={() => handleRequirementChange(requirement)}
                    />
                    <span>{requirement}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label htmlFor="message">{t('quote.fields.message')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t('quote.placeholders.message')}
                rows={4}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="button-primary submit-button"
            >
              {isSubmitting ? t('common.submitting') : t('quote.submit')}
            </button>
            <p className="form-note">
              {t('quote.corporate.formNote')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CorporateQuote;